'use client';

import { useEffect, useRef, useState } from 'react';
import Script from 'next/script';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
}

const POLL_INTERVAL_MS = 100;
const POLL_TIMEOUT_MS = 15000;

/**
 * Cloudflare Turnstile widget. Renders nothing (and the caller's onVerify
 * never fires) when NEXT_PUBLIC_TURNSTILE_SITE_KEY isn't set — matching the
 * server-side verifyTurnstileToken()'s graceful no-op in lib/turnstile.ts,
 * so a form using this component keeps working before Turnstile is set up.
 *
 * Two deliberate hardening choices, both found the hard way against a real
 * deploy where the widget silently never appeared:
 *
 * 1. `mounted` gate — SSR and the first client hydration pass both
 *    unconditionally render null, regardless of siteKey. Only a state
 *    update from a post-mount effect ever renders the real Script/div.
 *    This guarantees the server-rendered HTML and the client's first
 *    hydration pass are always identical for this component, so a
 *    third-party widget that injects its own DOM later can never be blamed
 *    for (or itself trigger) a React hydration mismatch.
 * 2. Poll for `window.turnstile` directly instead of relying solely on
 *    next/script's `onLoad` callback. next/script tracks script-loading
 *    state per `src` in a module-level cache; if a script with the same src
 *    was already requested by an earlier mount (e.g. this component
 *    remounting after an unrelated hydration error elsewhere on the page),
 *    `onLoad` is not guaranteed to fire again for the new instance even
 *    though the script itself loaded and `window.turnstile` is available.
 */
export function TurnstileWidget({ onVerify, onExpire }: TurnstileWidgetProps) {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || !siteKey || !containerRef.current) return;
    if (widgetIdRef.current) return;

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const tryRender = (): boolean => {
      if (cancelled || widgetIdRef.current || !containerRef.current || !window.turnstile) {
        return false;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: onVerify,
        'expired-callback': onExpire,
      });
      return true;
    };

    if (!tryRender()) {
      intervalId = setInterval(() => {
        if (tryRender() && intervalId) {
          clearInterval(intervalId);
        }
      }, POLL_INTERVAL_MS);
      timeoutId = setTimeout(() => {
        if (intervalId) clearInterval(intervalId);
      }, POLL_TIMEOUT_MS);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, siteKey]);

  if (!mounted || !siteKey) {
    return null;
  }

  return (
    <>
      <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" strategy="afterInteractive" />
      <div ref={containerRef} className="flex justify-center" />
    </>
  );
}

export function isTurnstileConfiguredClientSide(): boolean {
  return !!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
}
